import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Breadcrumbs from "../../styles/components/Breadcrumbs";
import ConfirmDialog from "../../component/ui/ConfirmDialog";
import useApiProfile from "../../hooks/useApiProfile";
import { API_BASE } from "../../config/apiBase";
import { getInterviewRoleLabel } from "../../utils/interviewRoles";
import "../../styles/pages/content/_administration-users.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const ROLE_OPTIONS = [
    { value: "owner", label: "Owner" },
    { value: "admin", label: "Admin" },
    { value: "editor", label: "Editor" },
    { value: "support", label: "Support" },
    { value: "billing", label: "Billing" },
    { value: "user", label: "User" },
    { value: "viewer", label: "Viewer" },
];
const ROLE_DESCRIPTIONS = {
    owner: "Full access across all admin areas and billing.",
    admin: "Manage users, content, and analytics. No billing access.",
    editor: "Manage content and view analytics. No user or billing access.",
    support: "Manage users and notes. No billing or analytics access.",
    billing: "Manage plans and subscriptions only.",
    user: "No admin access. View self account only.",
    // viewer: "Read-only admin access.",
};
const PLAN_OPTIONS = [
    { value: "free", label: "Free" },
    { value: "pro_ai", label: "Pro AI" },
];
const PLAN_DESCRIPTIONS = {
    free: "Core access for interview practice and account basics.",
    pro_ai: "Unlock AI feedback, deeper insights, and premium prep tools.",
};
const PAGE_SIZE = 20;

// Permission definitions — single source of truth for labels, keys, and descriptions
const PERM_DEFS = [
    {
        key: "canViewAdmin",
        label: "View admin",
        desc: "Read-only access to the admin dashboard.",
    },
    {
        key: "canManageUsers",
        label: "Manage users",
        desc: "Edit roles, permissions, and delete accounts.",
    },
    {
        key: "canManageContent",
        label: "Manage content",
        desc: "Edit help articles, FAQs, and flagged content.",
    },
    {
        key: "canViewAnalytics",
        label: "View analytics",
        desc: "Access usage stats and analytics dashboards.",
    },
    {
        key: "canManageBilling",
        label: "Manage billing",
        desc: "Change plans, billing, and subscription settings.",
    },
];

// Permissions auto-applied when a role is selected
const ROLE_PRESETS = {
    owner: {
        canViewAdmin: true,
        canManageUsers: true,
        canManageContent: true,
        canViewAnalytics: true,
        canManageBilling: true,
    },
    admin: {
        canViewAdmin: true,
        canManageUsers: true,
        canManageContent: true,
        canViewAnalytics: true,
        canManageBilling: false,
    },
    editor: {
        canViewAdmin: true,
        canManageUsers: false,
        canManageContent: true,
        canViewAnalytics: true,
        canManageBilling: false,
    },
    support: {
        canViewAdmin: true,
        canManageUsers: true,
        canManageContent: false,
        canViewAnalytics: false,
        canManageBilling: false,
    },
    billing: {
        canViewAdmin: true,
        canManageUsers: false,
        canManageContent: false,
        canViewAnalytics: false,
        canManageBilling: true,
    },
    user: {
        canViewAdmin: false,
        canManageUsers: false,
        canManageContent: false,
        canViewAnalytics: false,
        canManageBilling: false,
    },
    viewer: {
        canViewAdmin: true,
        canManageUsers: false,
        canManageContent: false,
        canViewAnalytics: false,
        canManageBilling: false,
    },
};

const EMPTY_PERMS = {
    canViewAdmin: false,
    canManageUsers: false,
    canManageContent: false,
    canViewAnalytics: false,
    canManageBilling: false,
};

const formatScore = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return (Math.trunc(n * 10) / 10).toFixed(1);
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconSearch = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <circle
            cx="6.5"
            cy="6.5"
            r="4.5"
            stroke="currentColor"
            strokeWidth="1.3"
        />
        <path
            d="M10 10l3 3"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
        />
    </svg>
);
const IconX = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 13 13"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2.5 2.5l8 8M10.5 2.5l-8 8"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
        />
    </svg>
);
const IconCheck = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 13 13"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2.5 7l3 3 5-5.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
const IconDownload = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M7 2v7M4.5 6.5L7 9l2.5-2.5M2 12h10"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
const IconChevUp = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2 8l4-4 4 4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
const IconChevDown = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
const IconChevRight = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M5 2.5l4 4.5-4 4.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
const IconChevLeft = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M9 2.5L5 7l4 4.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name) => {
    if (!name) return "?";
    return name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
};
const planLabel = (raw) =>
    String(raw || "")
        .toLowerCase()
        .replace(/[\s-]/g, "_") === "pro_ai"
        ? "Pro AI"
        : "Free";
const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString([], {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};
const fmtDateTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString([], {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};
const fmtDateTimeParts = (iso) => {
    if (!iso) return { date: "—", time: "" };
    const value = new Date(iso);
    return {
        date: value.toLocaleDateString([], {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }),
        time: value.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        }),
    };
};
const formatName = (nm) => {
    if (!nm) return "—";
    const full = `${nm.first_name || ""} ${nm.last_name || ""}`.trim();
    return full || "—";
};

// True when current perms exactly match the role's preset
const matchesPreset = (perms, role) => {
    const preset = ROLE_PRESETS[role];
    return preset
        ? PERM_DEFS.every(
              (d) => Boolean(perms[d.key]) === Boolean(preset[d.key])
          )
        : false;
};

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, onDismiss }) {
    if (!message || typeof document === "undefined") return null;
    return createPortal(
        <div
            className={`adm-users-toast adm-users-toast--${message.type}`}
            role="status"
            aria-live="polite"
        >
            {message.type === "success" ? <IconCheck /> : <IconX />}
            <span>{message.text}</span>
            <button
                type="button"
                className="adm-users-toast__close"
                onClick={onDismiss}
                aria-label="Dismiss"
            >
                <IconX />
            </button>
        </div>,
        document.body
    );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
    return (
        <div
            className={`adm-users-stat-card adm-users-stat-card--${accent || "default"}`}
        >
            <span className="adm-users-stat-card__value">{value}</span>
            <span className="adm-users-stat-card__label">{label}</span>
            {sub && <span className="adm-users-stat-card__sub">{sub}</span>}
        </div>
    );
}

// ─── Admin history ────────────────────────────────────────────────────────────
function AdminHistory({ log }) {
    if (!log || log.length === 0) {
        return (
            <div className="adm-users-plan-log__empty">
                <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    aria-hidden="true"
                >
                    <circle
                        cx="16"
                        cy="16"
                        r="13"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        opacity=".3"
                    />
                    <path
                        d="M16 10v7M16 20v2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        opacity=".5"
                    />
                </svg>
                <p>No actions recorded yet.</p>
            </div>
        );
    }
    return (
        <div className="adm-users-plan-log">
            {log.map((entry, i) => (
                <div className="adm-users-plan-log__entry" key={entry.id || i}>
                    <div
                        className="adm-users-plan-log__timeline"
                        aria-hidden="true"
                    >
                        <div className="adm-users-plan-log__dot" />
                        {i < log.length - 1 && (
                            <div className="adm-users-plan-log__line" />
                        )}
                    </div>
                    <div className="adm-users-plan-log__content">
                        {entry.type === "plan_change" ? (
                            <div className="adm-users-plan-log__pills">
                                <span
                                    className={`adm-users-pill adm-users-pill--${entry.from === "Pro AI" ? "pro" : "free"}`}
                                >
                                    {entry.from}
                                </span>
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    aria-label="changed to"
                                    className="adm-users-plan-log__arrow"
                                >
                                    <path
                                        d="M2 6h8M7 3l3 3-3 3"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span
                                    className={`adm-users-pill adm-users-pill--${entry.to === "Pro AI" ? "pro" : "free"}`}
                                >
                                    {entry.to}
                                </span>
                            </div>
                        ) : entry.type === "name_change" ? (
                            <div className="adm-users-plan-log__pills">
                                <span className="adm-users-pill adm-users-pill--neutral">
                                    {formatName(entry.meta?.from)}
                                </span>
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    aria-label="changed to"
                                    className="adm-users-plan-log__arrow"
                                >
                                    <path
                                        d="M2 6h8M7 3l3 3-3 3"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="adm-users-pill adm-users-pill--neutral">
                                    {formatName(entry.meta?.to)}
                                </span>
                            </div>
                        ) : entry.type === "role_change" ? (
                            <div className="adm-users-plan-log__pills">
                                <span className="adm-users-pill adm-users-pill--neutral">
                                    {entry.meta?.from || "—"}
                                </span>
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    aria-label="changed to"
                                    className="adm-users-plan-log__arrow"
                                >
                                    <path
                                        d="M2 6h8M7 3l3 3-3 3"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="adm-users-pill adm-users-pill--neutral">
                                    {entry.meta?.to || "—"}
                                </span>
                            </div>
                        ) : entry.type === "email_change" ? (
                            <div className="adm-users-plan-log__pills">
                                <span className="adm-users-pill adm-users-pill--neutral">
                                    {entry.meta?.from || "—"}
                                </span>
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    aria-label="changed to"
                                    className="adm-users-plan-log__arrow"
                                >
                                    <path
                                        d="M2 6h8M7 3l3 3-3 3"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="adm-users-pill adm-users-pill--neutral">
                                    {entry.meta?.to || "—"}
                                </span>
                            </div>
                        ) : entry.type === "interview_role_change" ? (
                            <div className="adm-users-plan-log__pills">
                                <span className="adm-users-pill adm-users-pill--neutral">
                                    {getInterviewRoleLabel(entry.meta?.from) ||
                                        "No role selected"}
                                </span>
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    aria-label="changed to"
                                    className="adm-users-plan-log__arrow"
                                >
                                    <path
                                        d="M2 6h8M7 3l3 3-3 3"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="adm-users-pill adm-users-pill--neutral">
                                    {getInterviewRoleLabel(entry.meta?.to) ||
                                        "No role selected"}
                                </span>
                            </div>
                        ) : entry.type === "password_reset_requested" ? (
                            <div className="adm-users-plan-log__title">
                                Password reset requested
                            </div>
                        ) : entry.type === "account_reset" ? (
                            <div className="adm-users-plan-log__title">
                                Account reset
                            </div>
                        ) : (
                            <div className="adm-users-plan-log__title">
                                {entry.type === "notes_update"
                                    ? "Updated admin notes"
                                    : "Admin action"}
                            </div>
                        )}
                        <div className="adm-users-plan-log__meta">
                            <span className="adm-users-plan-log__date">
                                {fmtDateTime(entry.created_at)}
                            </span>
                            {entry.actor && (
                                <span className="adm-users-plan-log__by">
                                    by {entry.actor}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── Role select (combobox) ────────────────────────────────────────────────────
function RoleSelect({ role, onChange, disabled, variant = "" }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const listRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        return () =>
            document.removeEventListener("mousedown", handlePointerDown);
    }, [open]);

    // Focus the selected (or first) option when the list opens
    useEffect(() => {
        if (!open) return;
        const active =
            listRef.current?.querySelector('[aria-selected="true"]') ||
            listRef.current?.querySelector('[role="option"]');
        active?.focus();
    }, [open]);

    const closeList = (focusTrigger = true) => {
        setOpen(false);
        if (focusTrigger) triggerRef.current?.focus();
    };

    const handleListKeyDown = (e) => {
        const items = Array.from(
            listRef.current?.querySelectorAll('[role="option"]') || []
        );
        const idx = items.indexOf(document.activeElement);
        if (e.key === "Escape") {
            e.stopPropagation();
            closeList();
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            items[(idx + 1) % items.length]?.focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            items[(idx - 1 + items.length) % items.length]?.focus();
        } else if (e.key === "Home") {
            e.preventDefault();
            items[0]?.focus();
        } else if (e.key === "End") {
            e.preventDefault();
            items[items.length - 1]?.focus();
        } else if (e.key === "Tab") {
            closeList(false);
        }
    };

    return (
        <div
            className={`adm-users-role-select${variant ? ` adm-users-role-select--${variant}` : ""}`}
            ref={rootRef}
        >
            <button
                type="button"
                ref={triggerRef}
                className="adm-users-role-select__summary"
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="adm-users-role-select__summary-text">
                    <span className="adm-users-role-select__summary-title">
                        {ROLE_OPTIONS.find((o) => o.value === role)?.label ||
                            "Select role"}
                    </span>
                    <span className="adm-users-role-select__summary-desc">
                        {ROLE_DESCRIPTIONS[role] || ""}
                    </span>
                </span>
                <span
                    className="adm-users-role-select__summary-icon"
                    aria-hidden="true"
                >
                    <IconChevDown />
                </span>
            </button>
            {open && (
                <div
                    className="adm-users-role-select__list"
                    role="listbox"
                    aria-label="Role"
                    ref={listRef}
                    onKeyDown={handleListKeyDown}
                >
                    {ROLE_OPTIONS.map((o) => (
                        <button
                            key={o.value}
                            type="button"
                            role="option"
                            aria-selected={o.value === role}
                            className={`adm-users-role-select__item${o.value === role ? " is-active" : ""}`}
                            onClick={() => {
                                onChange(o.value);
                                closeList();
                            }}
                        >
                            <span className="adm-users-role-select__item-title">
                                {o.label}
                            </span>
                            <span className="adm-users-role-select__item-desc">
                                {ROLE_DESCRIPTIONS[o.value] || ""}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Permissions group ────────────────────────────────────────────────────────
function PermissionsGroup({
    role,
    perms,
    onRoleChange,
    onPermChange,
    disabled,
}) {
    const enabledCount = PERM_DEFS.filter((d) => perms[d.key]).length;
    const allOn = enabledCount === PERM_DEFS.length;
    const isUserRole = role === "user";
    // const isCustomised = !matchesPreset(perms, role);

    const handleRoleChange = (nextRole) => {
        onRoleChange(nextRole);
        const preset = ROLE_PRESETS[nextRole];
        if (preset)
            PERM_DEFS.forEach((d) =>
                onPermChange(d.key, preset[d.key] ?? false)
            );
    };

    return (
        <div className="adm-users-drawer__group">
            <p className="adm-users-drawer__group-label">Permissions</p>

            <div className="adm-users-drawer__field-info">
                <span className="adm-users-drawer__field-title">Roles</span>
                {/* <span className="adm-users-drawer__field-sub">
                    Selecting a role applies a permission preset.
                </span> */}
                <p className="adm-users-drawer__group-sub">
                    Selecting a role applies a permission preset.
                </p>
            </div>

            {/* Permission matrix */}
            <div
                className="adm-users-perms"
                role="group"
                aria-label="Permissions"
            >
                {/* Matrix header */}
                <div className="adm-users-perms__head adm-users-perms__head--role-select">
                    <span className="adm-users-perms__head-label">
                        <RoleSelect
                            role={role}
                            onChange={handleRoleChange}
                            disabled={disabled}
                            variant="perms"
                        />
                    </span>
                </div>

                {isUserRole && (
                    <div className="adm-users-perms__row adm-users-perms__row--on">
                        <div className="adm-users-perms__row-text">
                            <span className="adm-users-perms__row-label">
                                View self
                            </span>
                            <span className="adm-users-perms__row-desc">
                                Access only this user’s own account settings.
                            </span>
                        </div>
                        <div className="adm-users-perms__row-right">
                            <span className="adm-users-perms__status adm-users-perms__status--on">
                                On
                            </span>
                        </div>
                    </div>
                )}

                {/* One row per permission — read-only: reflects the preset, not editable */}
                {PERM_DEFS.map(({ key, label, desc }) => {
                    const isOn = Boolean(perms[key]);
                    return (
                        <div
                            key={key}
                            className={`adm-users-perms__row${isOn ? " adm-users-perms__row--on" : ""}`}
                        >
                            <div className="adm-users-perms__row-text">
                                <span className="adm-users-perms__row-label">
                                    {label}
                                </span>
                                <span className="adm-users-perms__row-desc">
                                    {desc}
                                </span>
                            </div>
                            <div className="adm-users-perms__row-right">
                                <span
                                    className={`adm-users-perms__status${isOn ? " adm-users-perms__status--on" : ""}`}
                                >
                                    {isOn ? "On" : "Off"}
                                </span>
                            </div>
                        </div>
                    );
                })}

                {/* Footer summary */}
                {/* <div className="adm-users-perms__foot">
                    {isUserRole
                        ? "User access only — no admin permissions."
                        : enabledCount === 0
                          ? "No permissions granted — this user cannot access admin."
                          : allOn
                            ? "Full access granted across all admin areas."
                            : `${enabledCount} of ${PERM_DEFS.length} permissions enabled.`}
                </div> */}
            </div>
        </div>
    );
}

// ─── User detail drawer ───────────────────────────────────────────────────────
function UserDrawer({
    user,
    onClose,
    onToggleSuspend,
    onUpdate,
    onPersist,
    onReset,
    onDelete,
    authedFetch,
    setMessage,
    currentProfile,
    refreshKey,
}) {
    const [historyLog, setHistoryLog] = useState([]);
    const [logLoading, setLogLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("overview");
    const [draftPlan, setDraftPlan] = useState("free");
    const [initialPlan, setInitialPlan] = useState("free");
    const [notes, setNotes] = useState("");
    const [initialNotes, setInitialNotes] = useState("");
    const [notesLoading, setNotesLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [draftRole, setDraftRole] = useState("user");
    const [initialRole, setInitialRole] = useState("user");
    const [draftPerms, setDraftPerms] = useState(EMPTY_PERMS);
    const [initialPerms, setInitialPerms] = useState(EMPTY_PERMS);

    useEffect(() => {
        if (!user) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, [user]);

    useEffect(() => {
        const h = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose]);

    useEffect(() => {
        if (!user) return;
        setLogLoading(true);
        setHistoryLog([]);
        (async () => {
            try {
                // Bust any intermediary/browser caching so the history reflects
                // just-completed actions (e.g. account reset) immediately.
                const res = await authedFetch(
                    `/admin/users/${user.id}/history?v=${encodeURIComponent(String(refreshKey || 0))}`,
                    { cache: "no-store" }
                );
                if (!res.ok) return;
                const data = await res.json();
                setHistoryLog(
                    (data || []).map((e) =>
                        e.type === "plan_change"
                            ? {
                                  ...e,
                                  from: planLabel(e.meta?.from),
                                  to: planLabel(e.meta?.to),
                              }
                            : e
                    )
                );
            } catch {
            } finally {
                setLogLoading(false);
            }
        })();
    }, [user, authedFetch, refreshKey]);

    useEffect(() => {
        if (!user) return;
        setNotesLoading(true);
        setNotes("");
        setInitialNotes("");
        (async () => {
            try {
                const res = await authedFetch(`/admin/users/${user.id}/notes`);
                if (!res.ok) return;
                const data = await res.json();
                const n = data?.notes || "";
                setNotes(n);
                setInitialNotes(n);
            } catch {
            } finally {
                setNotesLoading(false);
            }
        })();
    }, [user, authedFetch]);

    useEffect(() => {
        if (!user) return;
        const p = user.plan === "Pro AI" ? "pro_ai" : "free";
        setDraftPlan(p);
        setInitialPlan(p);
    }, [user]);

    useEffect(() => {
        if (!user) return;
        const r = user.adminRole || "user";
        const preset = ROLE_PRESETS[r] || EMPTY_PERMS;
        const p = { ...preset };
        setDraftRole(r);
        setInitialRole(r);
        setDraftPerms(p);
        setInitialPerms(p);
    }, [user]);

    const hasPlanChange = draftPlan !== initialPlan;
    const hasNotesChange = notes !== initialNotes;
    const hasAdminChange =
        draftRole !== initialRole ||
        PERM_DEFS.some((d) => draftPerms[d.key] !== initialPerms[d.key]);
    const hasChanges = hasPlanChange || hasNotesChange || hasAdminChange;

    const handleSaveChanges = async () => {
        if (isSelfLocked) {
            setSaveError("Only owners can edit their own admin account.");
            return;
        }
        if (!user || !hasChanges) return;
        setSaveLoading(true);
        setSaveError(null);
        try {
            if (hasPlanChange) {
                const res = await authedFetch("/admin/users/plan", {
                    method: "PUT",
                    body: JSON.stringify({ userId: user.id, plan: draftPlan }),
                });
                if (!res.ok)
                    throw new Error(
                        (await res.text()) || "Failed to update plan."
                    );
                onUpdate(user.id, { plan: planLabel(draftPlan) });
                setInitialPlan(draftPlan);
            }
            if (hasNotesChange) {
                const res = await authedFetch(`/admin/users/${user.id}/notes`, {
                    method: "PUT",
                    body: JSON.stringify({ notes }),
                });
                if (!res.ok)
                    throw new Error(
                        (await res.text()) || "Failed to save notes."
                    );
                const data = await res.json();
                const n = data?.notes || "";
                setNotes(n);
                setInitialNotes(n);
            }
            if (hasAdminChange) {
                const updated = await onPersist({
                    ...user,
                    adminRole: draftRole,
                    ...draftPerms,
                });
                if (updated) {
                    const r = updated.admin_role || draftRole;
                    const p = {
                        canViewAdmin: Boolean(updated.can_view_admin),
                        canManageUsers: Boolean(updated.can_manage_users),
                        canManageContent: Boolean(updated.can_manage_content),
                        canViewAnalytics: Boolean(updated.can_view_analytics),
                        canManageBilling: Boolean(updated.can_manage_billing),
                    };
                    setDraftRole(r);
                    setInitialRole(r);
                    setDraftPerms(p);
                    setInitialPerms(p);
                } else {
                    setInitialRole(draftRole);
                    setInitialPerms(draftPerms);
                }
            }
            setMessage({ type: "success", text: "Changes saved." });
        } catch (err) {
            setSaveError(err?.message || "Failed to save changes.");
        } finally {
            setSaveLoading(false);
        }
    };

    if (!user || typeof document === "undefined") return null;
    const isSuspended = user.status === "suspended";
    const isOwner =
        (currentProfile?.admin_role || currentProfile?.adminRole) === "owner";
    const isSelf =
        (currentProfile?.id && currentProfile.id === user.id) ||
        (currentProfile?.email && currentProfile.email === user.email);
    const isSelfLocked = isSelf && !isOwner;

    return createPortal(
        <div
            className="adm-users-drawer-overlay"
            aria-modal="true"
            role="dialog"
            aria-label={`User details: ${user.name}`}
        >
            <div
                className="adm-users-drawer-backdrop"
                onClick={onClose}
                aria-hidden="true"
            />
            <aside className="adm-users-drawer">
                <div className="adm-users-drawer__hero">
                    <button
                        type="button"
                        className="adm-users-drawer__close"
                        onClick={onClose}
                        aria-label="Close drawer"
                    >
                        <IconX />
                    </button>
                    <div className="adm-users-drawer__hero-body">
                        <div className="adm-users-drawer__avatar-lg">
                            {getInitials(user.name)}
                        </div>
                        <div className="adm-users-drawer__hero-text">
                            <p className="adm-users-drawer__name">
                                {user.name}
                            </p>
                            <p className="adm-users-drawer__email">
                                {user.email}
                            </p>
                        </div>
                        <div className="adm-users-drawer__hero-badges">
                            <span
                                className={`adm-users-pill adm-users-pill--${user.status}`}
                            >
                                {user.status}
                            </span>
                            <span
                                className={`adm-users-pill adm-users-pill--${user.plan === "Pro AI" ? "pro" : "free"}`}
                            >
                                {user.plan}
                            </span>
                        </div>
                    </div>
                    <div className="adm-users-drawer__kpis">
                        <div className="adm-users-drawer__kpi">
                            <span className="adm-users-drawer__kpi-val">
                                {user.recordings ?? "—"}
                            </span>
                            <span className="adm-users-drawer__kpi-label">
                                Recordings
                            </span>
                        </div>
                        <div className="adm-users-drawer__kpi">
                            <span className="adm-users-drawer__kpi-val">
                                {user.avgScore != null
                                    ? `${formatScore(user.avgScore)}/10`
                                    : "—"}
                            </span>
                            <span className="adm-users-drawer__kpi-label">
                                Avg score
                            </span>
                        </div>
                        <div className="adm-users-drawer__kpi">
                            <span className="adm-users-drawer__kpi-val">
                                {fmtDate(user.joinedAt)}
                            </span>
                            <span className="adm-users-drawer__kpi-label">
                                Joined
                            </span>
                        </div>
                        <div className="adm-users-drawer__kpi">
                            <span className="adm-users-drawer__kpi-val">
                                {fmtDate(user.lastActiveAt)}
                            </span>
                            <span className="adm-users-drawer__kpi-label">
                                Last active
                            </span>
                        </div>
                    </div>
                </div>

                <div className="adm-users-drawer__tabs" role="tablist">
                    {[
                        { id: "overview", label: "Overview" },
                        { id: "history", label: "History" },
                    ].map(({ id, label }) => (
                        <button
                            key={id}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === id}
                            className={`adm-users-drawer__tab${activeTab === id ? " is-active" : ""}`}
                            onClick={() => setActiveTab(id)}
                        >
                            {label}
                            {id === "history" && historyLog.length > 0 && (
                                <span className="adm-users-drawer__tab-badge">
                                    {historyLog.length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="adm-users-drawer__body">
                    {activeTab === "overview" && (
                        <>
                            {/* Plan */}
                            <div className="adm-users-drawer__group">
                                <p className="adm-users-drawer__group-label">
                                    Subscription
                                </p>
                                <div className="adm-users-drawer__field-row">
                                    <div className="adm-users-drawer__field-info">
                                        <span className="adm-users-drawer__field-title">
                                            Current plan
                                        </span>
                                        <span className="adm-users-drawer__field-sub">
                                            Manage this user's subscription plan
                                            and access level.
                                        </span>
                                    </div>
                                    <details
                                        className="adm-users-role-select adm-users-role-select--plan"
                                        aria-disabled={
                                            saveLoading || isSelfLocked
                                        }
                                    >
                                        <summary
                                            className="adm-users-role-select__summary"
                                            onClick={(e) => {
                                                if (saveLoading || isSelfLocked)
                                                    e.preventDefault();
                                            }}
                                        >
                                            <span className="adm-users-role-select__summary-text">
                                                <span className="adm-users-role-select__summary-title">
                                                    {PLAN_OPTIONS.find(
                                                        (o) =>
                                                            o.value ===
                                                            draftPlan
                                                    )?.label || "Select plan"}
                                                </span>
                                                <span className="adm-users-role-select__summary-desc">
                                                    {PLAN_DESCRIPTIONS[
                                                        draftPlan
                                                    ] || ""}
                                                </span>
                                            </span>
                                            <span className="adm-users-role-select__summary-icon">
                                                <IconChevDown />
                                            </span>
                                        </summary>
                                        <div className="adm-users-role-select__list">
                                            {PLAN_OPTIONS.map((o) => (
                                                <button
                                                    key={o.value}
                                                    type="button"
                                                    className={`adm-users-role-select__item${o.value === draftPlan ? " is-active" : ""}`}
                                                    disabled={
                                                        saveLoading ||
                                                        isSelfLocked
                                                    }
                                                    onClick={(e) => {
                                                        setDraftPlan(o.value);
                                                        const details =
                                                            e.currentTarget.closest(
                                                                "details"
                                                            );
                                                        if (details)
                                                            details.removeAttribute(
                                                                "open"
                                                            );
                                                    }}
                                                >
                                                    <span className="adm-users-role-select__item-title">
                                                        {o.label}
                                                    </span>
                                                    <span className="adm-users-role-select__item-desc">
                                                        {PLAN_DESCRIPTIONS[
                                                            o.value
                                                        ] || ""}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </details>
                                </div>
                            </div>

                            {/* Role & permissions — redesigned */}
                            <PermissionsGroup
                                role={draftRole}
                                perms={draftPerms}
                                onRoleChange={setDraftRole}
                                onPermChange={(key, val) =>
                                    setDraftPerms((prev) => ({
                                        ...prev,
                                        [key]: val,
                                    }))
                                }
                                disabled={isSelfLocked}
                            />

                            {/* Notes */}
                            <div className="adm-users-drawer__group">
                                <p className="adm-users-drawer__group-label">
                                    Notes
                                </p>
                                <div className="adm-users-notes">
                                    <textarea
                                        className="adm-users-notes__input"
                                        value={notes}
                                        placeholder={
                                            notesLoading
                                                ? "Loading notes…"
                                                : "Write a private note for the admin team."
                                        }
                                        onChange={(e) =>
                                            setNotes(e.target.value)
                                        }
                                        rows={4}
                                        disabled={
                                            notesLoading ||
                                            saveLoading ||
                                            isSelfLocked
                                        }
                                    />
                                </div>
                            </div>

                            {/* Contact */}
                            <div className="adm-users-drawer__group">
                                <p className="adm-users-drawer__group-label">
                                    Contact
                                </p>
                                <a
                                    className="adm-users-drawer__contact-row"
                                    href={`mailto:${user.email}`}
                                    aria-label={`Email ${user.name}`}
                                >
                                    <div
                                        className="adm-users-drawer__contact-icon"
                                        aria-hidden="true"
                                    >
                                        <svg
                                            width="15"
                                            height="15"
                                            viewBox="0 0 15 15"
                                            fill="none"
                                        >
                                            <rect
                                                x="1"
                                                y="3"
                                                width="13"
                                                height="9"
                                                rx="1.5"
                                                stroke="currentColor"
                                                strokeWidth="1.2"
                                            />
                                            <path
                                                d="M1 5l6.5 4L14 5"
                                                stroke="currentColor"
                                                strokeWidth="1.2"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <span className="adm-users-drawer__contact-label">
                                            Send email
                                        </span>
                                        <span className="adm-users-drawer__contact-val">
                                            {user.email}
                                        </span>
                                    </div>
                                    <IconChevRight />
                                </a>
                            </div>
                        </>
                    )}

                    {activeTab === "history" && (
                        <div className="adm-users-drawer__group">
                            <p className="adm-users-drawer__group-label">
                                Change history
                            </p>
                            {logLoading ? (
                                <div className="adm-users-drawer__log-loading">
                                    <div
                                        className="adm-users-skeleton__row"
                                        style={{
                                            height: "52px",
                                            borderRadius: "8px",
                                        }}
                                    />
                                    <div
                                        className="adm-users-skeleton__row"
                                        style={{
                                            height: "52px",
                                            borderRadius: "8px",
                                        }}
                                    />
                                </div>
                            ) : (
                                <AdminHistory log={historyLog} />
                            )}
                        </div>
                    )}
                </div>

                <div className="adm-users-drawer__footer">
                    {isSelfLocked && (
                        <span className="adm-users-drawer__save-error">
                            Only owners can edit their own admin account.
                        </span>
                    )}
                    {saveError && (
                        <span className="adm-users-drawer__save-error">
                            {saveError}
                        </span>
                    )}
                    <button
                        type="button"
                        className="adm-users-btn adm-users-btn--sm"
                        onClick={handleSaveChanges}
                        disabled={!hasChanges || saveLoading || isSelfLocked}
                    >
                        {saveLoading ? "Saving…" : "Save changes"}
                    </button>
                    {/* <button
                        type="button"
                        className="adm-users-btn adm-users-btn--ghost adm-users-btn--sm btn-reset"
                        onClick={() => onReset?.(user)}
                        disabled={isSelfLocked}
                    >
                        Reset account
                    </button> */}
                    <button
                        type="button"
                        className={`adm-users-btn adm-users-btn--sm adm-users-btn--ghost${isSuspended ? " adm-users-btn--reinstate" : " adm-users-btn--suspend"}`}
                        onClick={() => onToggleSuspend(user)}
                        disabled={isSelfLocked}
                    >
                        {isSuspended ? "Reinstate account" : "Suspend account"}
                    </button>
                    <button
                        type="button"
                        className="adm-users-btn adm-users-btn--ghost adm-users-btn--sm adm-users-btn--danger"
                        onClick={() => onDelete(user)}
                        disabled={isSelfLocked}
                    >
                        Delete account
                    </button>
                </div>
            </aside>
        </div>,
        document.body
    );
}

// ─── User row ─────────────────────────────────────────────────────────────────
function UserRow({ user, selected, onSelect, onOpenDrawer }) {
    const latestLogin = fmtDateTimeParts(user.latestLogin);

    return (
        <div
            className={`adm-users-user-row${selected ? " adm-users-user-row--selected" : ""}${user.status === "suspended" ? " adm-users-user-row--suspended" : ""}`}
            role="row"
        >
            <div role="cell" className="adm-users-user-row__check">
                <input
                    type="checkbox"
                    className="adm-users-checkbox"
                    checked={selected}
                    onChange={onSelect}
                    aria-label={`Select ${user.name}`}
                />
            </div>
            <button
                type="button"
                className="adm-users-user-row__identity adm-users-user-row__identity--btn"
                role="cell"
                onClick={() => onOpenDrawer(user)}
            >
                <div className="adm-users-user-row__avatar">
                    {getInitials(user.name)}
                </div>
                <div className="adm-users-user-row__meta">
                    <span className="adm-users-user-row__name">
                        {user.name}
                    </span>
                    <span className="adm-users-user-row__email">
                        {user.email}
                    </span>
                    <span className="adm-users-user-row__user-id">
                        {user.userId}
                    </span>
                </div>
                <IconChevRight />
            </button>
            <div role="cell">
                <span
                    className={`adm-users-pill adm-users-pill--${user.status}`}
                >
                    {user.status}
                </span>
            </div>
            <div role="cell" className="adm-users-user-row__plan">
                <span
                    className={`adm-users-pill ${user.plan === "Pro AI" ? "adm-users-pill--pro" : "adm-users-pill--free"}`}
                >
                    {user.plan}
                </span>
            </div>
            <div role="cell" className="adm-users-user-row__date">
                {fmtDate(user.joinedAt)}
            </div>
            <div role="cell" className="adm-users-user-row__count">
                {Number.isFinite(user.loginCount) ? user.loginCount : "—"}
            </div>
            <div role="cell" className="adm-users-user-row__connection">
                {user.connection || "—"}
            </div>
            <div
                role="cell"
                className="adm-users-user-row__date adm-users-user-row__date--stacked"
            >
                <span>{latestLogin.date}</span>
                {latestLogin.time ? <span>{latestLogin.time}</span> : null}
            </div>
        </div>
    );
}

// ─── Bulk action bar ──────────────────────────────────────────────────────────
function BulkBar({
    count,
    onClear,
    onBulkPlan,
    onBulkDelete,
    onExportSelected,
}) {
    if (count === 0) return null;
    return createPortal(
        <div
            className="adm-users-bulk-bar"
            role="toolbar"
            aria-label="Bulk actions"
        >
            <span className="adm-users-bulk-bar__count">{count} selected</span>
            <div className="adm-users-bulk-bar__actions">
                <select
                    className="adm-users-select adm-users-select--sm"
                    defaultValue=""
                    onChange={(e) => {
                        if (e.target.value) onBulkPlan(e.target.value);
                        e.target.value = "";
                    }}
                    aria-label="Change plan for selected"
                >
                    <option value="" disabled>
                        Change plan…
                    </option>
                    {PLAN_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    className="adm-users-btn adm-users-btn--ghost adm-users-btn--sm"
                    onClick={onExportSelected}
                >
                    <IconDownload /> Export selected
                </button>
                <button
                    type="button"
                    className="adm-users-btn adm-users-btn--ghost adm-users-btn--sm adm-users-btn--danger"
                    onClick={onBulkDelete}
                >
                    Delete selected
                </button>
                <button
                    type="button"
                    className="adm-users-btn adm-users-btn--ghost adm-users-btn--sm"
                    onClick={onClear}
                >
                    <IconX /> Clear
                </button>
            </div>
        </div>,
        document.body
    );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({ page, total, pageSize, onChange }) {
    const totalPages = Math.ceil(total / pageSize);
    if (totalPages <= 1) return null;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return (
        <div className="adm-users-pagination" aria-label="Pagination">
            <span className="adm-users-pagination__info">
                {start}–{end} of {total}
            </span>
            <div className="adm-users-pagination__controls">
                <button
                    type="button"
                    className="adm-users-pagination__btn"
                    disabled={page === 1}
                    onClick={() => onChange(page - 1)}
                    aria-label="Previous page"
                >
                    <IconChevLeft />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                        (p) =>
                            p === 1 ||
                            p === totalPages ||
                            Math.abs(p - page) <= 1
                    )
                    .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                        acc.push(p);
                        return acc;
                    }, [])
                    .map((p, i) =>
                        p === "…" ? (
                            <span
                                key={`sep-${i}`}
                                className="adm-users-pagination__sep"
                            >
                                …
                            </span>
                        ) : (
                            <button
                                key={p}
                                type="button"
                                className={`adm-users-pagination__btn${page === p ? " is-active" : ""}`}
                                onClick={() => onChange(p)}
                            >
                                {p}
                            </button>
                        )
                    )}
                <button
                    type="button"
                    className="adm-users-pagination__btn"
                    disabled={page === totalPages}
                    onClick={() => onChange(page + 1)}
                    aria-label="Next page"
                >
                    <IconChevRight />
                </button>
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AdministrationUsers() {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const { profile: currentProfile } = useApiProfile();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [planFilter, setPlanFilter] = useState("all");
    const [sortField, setSortField] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [resetTarget, setResetTarget] = useState(null);
    const [drawerUser, setDrawerUser] = useState(null);
    const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
    const [page, setPage] = useState(1);
    const searchRef = useRef(null);
    const apiBase = API_BASE;

    useEffect(() => {
        if (!message) return;
        const t = setTimeout(() => setMessage(null), 4500);
        return () => clearTimeout(t);
    }, [message]);
    useEffect(() => {
        const h = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, []);

    const authedFetch = useCallback(
        async (path, options = {}) => {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
            });
            return fetch(`${apiBase}${path}`, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                },
            });
        },
        [apiBase, getAccessTokenSilently]
    );

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            setLoading(false);
            setError("Log in to view user management.");
            return;
        }
        let aborted = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await authedFetch("/admin/users");
                if (!res.ok)
                    throw new Error(
                        (await res.text()) || "Failed to load users."
                    );
                const data = await res.json();
                if (aborted) return;
                setUsers(
                    (data.users || []).map((u) => ({
                        id: u.id,
                        userId: u.id,
                        name:
                            `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() ||
                            u.email,
                        email: u.email,
                        status: u.status || "active",
                        plan: planLabel(u.plan),
                        adminRole: u.admin_role || "user",
                        canViewAdmin: Boolean(
                            u.can_view_admin ??
                                u.can_manage ??
                                u.can_bill ??
                                false
                        ),
                        canManageUsers: Boolean(
                            u.can_manage_users ?? u.can_manage
                        ),
                        canManageContent: Boolean(u.can_manage_content),
                        canViewAnalytics: Boolean(u.can_view_analytics),
                        canManageBilling: Boolean(
                            u.can_manage_billing ?? u.can_bill
                        ),
                        loginCount: Number(u.login_count || 0),
                        connection: u.connection || "",
                        latestLogin: u.last_login || null,
                        joinedAt: u.created_at || null,
                        lastActiveAt: u.last_active_at || null,
                        recordings: Number(u.recording_count || 0),
                        avgScore:
                            u.avg_feedback_score == null
                                ? null
                                : Number(u.avg_feedback_score),
                    }))
                );
            } catch (err) {
                if (!aborted) setError(err?.message || "Failed to load users.");
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [authedFetch, isAuthenticated, isLoading]);

    const updateUser = useCallback((id, updates) => {
        setUsers((prev) =>
            prev.map((u) => (u.id === id ? { ...u, ...updates } : u))
        );
        setDrawerUser((prev) =>
            prev?.id === id ? { ...prev, ...updates } : prev
        );
    }, []);

    const handleSave = useCallback(
        async (user) => {
            const res = await authedFetch(`/admin/users/${user.id}`, {
                method: "PUT",
                body: JSON.stringify({
                    adminRole: user.adminRole,
                    canViewAdmin: user.canViewAdmin,
                    canManageUsers: user.canManageUsers,
                    canManageContent: user.canManageContent,
                    canViewAnalytics: user.canViewAnalytics,
                    canManageBilling: user.canManageBilling,
                }),
            });
            if (!res.ok)
                throw new Error((await res.text()) || "Failed to update user.");
            const data = await res.json();
            updateUser(user.id, {
                adminRole: data.user.admin_role || user.adminRole,
                canViewAdmin: Boolean(data.user.can_view_admin),
                canManageUsers: Boolean(data.user.can_manage_users),
                canManageContent: Boolean(data.user.can_manage_content),
                canViewAnalytics: Boolean(data.user.can_view_analytics),
                canManageBilling: Boolean(data.user.can_manage_billing),
            });
            return data.user;
        },
        [authedFetch, updateUser]
    );

    const handleChangePlan = useCallback(
        async (userId, planValue) => {
            try {
                const res = await authedFetch("/admin/users/plan", {
                    method: "PUT",
                    body: JSON.stringify({ userId, plan: planValue }),
                });
                if (!res.ok)
                    throw new Error(
                        (await res.text()) || "Failed to update plan."
                    );
                updateUser(userId, { plan: planLabel(planValue) });
                setMessage({ type: "success", text: "Plan updated." });
            } catch (err) {
                setMessage({
                    type: "error",
                    text: err?.message || "Failed to update plan.",
                });
            }
        },
        [authedFetch, updateUser]
    );

    const handleToggleSuspend = useCallback(
        async (user) => {
            const nextStatus =
                user.status === "suspended" ? "active" : "suspended";
            try {
                const res = await authedFetch(
                    `/admin/users/${user.id}/status`,
                    {
                        method: "PUT",
                        body: JSON.stringify({ status: nextStatus }),
                    }
                );
                if (!res.ok)
                    throw new Error(
                        (await res.text()) || "Failed to update status."
                    );
                updateUser(user.id, { status: nextStatus });
                setMessage({
                    type: "success",
                    text: `${user.name} ${nextStatus === "suspended" ? "suspended" : "reinstated"}.`,
                });
            } catch (err) {
                setMessage({
                    type: "error",
                    text: err?.message || "Failed to update status.",
                });
            }
        },
        [authedFetch, updateUser]
    );

    const handleDelete = useCallback(
        async (user) => {
            if (!user?.id) return;
            try {
                const res = await authedFetch(`/admin/users/${user.id}`, {
                    method: "DELETE",
                });
                if (!res.ok)
                    throw new Error(
                        (await res.text()) || "Failed to delete user."
                    );
                setUsers((prev) => prev.filter((u) => u.id !== user.id));
                setSelectedIds((prev) => {
                    const n = new Set(prev);
                    n.delete(user.id);
                    return n;
                });
                if (drawerUser?.id === user.id) setDrawerUser(null);
                setMessage({ type: "success", text: `${user.name} deleted.` });
            } catch (err) {
                setMessage({
                    type: "error",
                    text: err?.message || "Failed to delete user.",
                });
            } finally {
                setDeleteTarget(null);
            }
        },
        [authedFetch, drawerUser]
    );

    const handleSort = (field) => {
        if (sortField === field)
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else {
            setSortField(field);
            setSortDir("asc");
        }
        setPage(1);
    };
    const SortIcon = ({ field }) => {
        if (sortField !== field)
            return (
                <span className="adm-users-table__sort-idle">
                    <IconChevDown />
                </span>
            );
        return sortDir === "asc" ? <IconChevUp /> : <IconChevDown />;
    };

    const filteredUsers = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = users.filter((u) => {
            if (
                q &&
                !u.name.toLowerCase().includes(q) &&
                !u.email.toLowerCase().includes(q) &&
                !String(u.userId || "")
                    .toLowerCase()
                    .includes(q)
            )
                return false;
            if (statusFilter !== "all" && u.status !== statusFilter)
                return false;
            if (
                planFilter !== "all" &&
                u.plan.toLowerCase().replace(/\s/g, "_") !== planFilter
            )
                return false;
            return true;
        });
        list.sort((a, b) => {
            let av = a[sortField] ?? "",
                bv = b[sortField] ?? "";
            if (typeof av === "string") av = av.toLowerCase();
            if (typeof bv === "string") bv = bv.toLowerCase();
            if (av < bv) return sortDir === "asc" ? -1 : 1;
            if (av > bv) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return list;
    }, [query, statusFilter, planFilter, users, sortField, sortDir]);

    const pagedUsers = useMemo(
        () => filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filteredUsers, page]
    );
    useEffect(() => {
        setPage(1);
    }, [query, statusFilter, planFilter, sortField, sortDir]);

    const allPageSelected =
        pagedUsers.length > 0 && pagedUsers.every((u) => selectedIds.has(u.id));
    const somePageSelected = pagedUsers.some((u) => selectedIds.has(u.id));
    const toggleSelectAll = () =>
        setSelectedIds((prev) => {
            const n = new Set(prev);
            if (allPageSelected) pagedUsers.forEach((u) => n.delete(u.id));
            else pagedUsers.forEach((u) => n.add(u.id));
            return n;
        });
    const handleBulkPlan = async (pv) => {
        for (const id of selectedIds) await handleChangePlan(id, pv);
        setSelectedIds(new Set());
    };
    const handleBulkDelete = () => {
        const first = users.find((u) => selectedIds.has(u.id));
        if (first) setDeleteTarget({ ...first, _bulkIds: [...selectedIds] });
    };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        if (deleteTarget._bulkIds) {
            for (const id of deleteTarget._bulkIds) {
                const u = users.find((x) => x.id === id);
                if (u) await handleDelete(u);
            }
            setSelectedIds(new Set());
        } else await handleDelete(deleteTarget);
    };

    const confirmReset = async () => {
        const target = resetTarget;
        if (!target?.id) return;
        try {
            const res = await authedFetch(`/admin/users/${target.id}/reset`, {
                method: "POST",
            });
            if (!res.ok) {
                const text = await res.text().catch(() => "");
                const looksLikeExpress404 =
                    text.includes("Cannot POST ") ||
                    text.trim().startsWith("<!DOCTYPE html");
                const msg =
                    looksLikeExpress404 && res.status === 404
                        ? "Reset endpoint not found (restart the server?)."
                        : text || `Failed to reset account (${res.status}).`;
                throw new Error(msg);
            }
            await res.json().catch(() => ({}));
            updateUser(target.id, {
                recordings: 0,
                avgScore: null,
                loginCount: 0,
                latestLogin: null,
                lastActiveAt: null,
            });
            setDrawerRefreshKey((v) => v + 1);
            setMessage({
                type: "success",
                text: `${target.name} account reset.`,
            });
        } catch (err) {
            setMessage({
                type: "error",
                text: err?.message || "Failed to reset account.",
            });
        } finally {
            setResetTarget(null);
        }
    };
    const exportRows = (rows) => {
        const h = [
            "Name",
            "Email",
            "Status",
            "Plan",
            "Role",
            "View Admin",
            "Manage Users",
            "Manage Content",
            "View Analytics",
            "Manage Billing",
            "Recordings",
            "Joined",
        ];
        const csv = [
            h,
            ...rows.map((u) => [
                u.name,
                u.email,
                u.status,
                u.plan,
                u.adminRole,
                u.canViewAdmin,
                u.canManageUsers,
                u.canManageContent,
                u.canViewAnalytics,
                u.canManageBilling,
                u.recordings,
                fmtDate(u.joinedAt),
            ]),
        ]
            .map((r) => r.join(","))
            .join("\n");
        const a = Object.assign(document.createElement("a"), {
            href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })),
            download: "mooz-users.csv",
        });
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const totalUsers = users.length;
    const proUsers = users.filter((u) => u.plan === "Pro AI").length;
    const activeUsers = users.filter((u) => u.status === "active").length;
    const totalRecs = users.reduce((a, u) => a + (u.recordings || 0), 0);
    const TABLE_COLS = [
        { field: "name", label: "User" },
        { field: "status", label: "Status" },
        { field: "plan", label: "Plan" },
        { field: "joinedAt", label: "Joined" },
        { field: "loginCount", label: "Logins" },
        { field: "connection", label: "Connection" },
        { field: "latestLogin", label: "Latest login" },
    ];

    return (
        <div className="adm-users" id="administration-users">
            <Toast message={message} onDismiss={() => setMessage(null)} />
            <ConfirmDialog
                open={Boolean(resetTarget)}
                title="Reset account"
                description={`Reset ${resetTarget?.name || "this user"}? This deletes all recordings and draft answers, clears their interview role, and resets login count so onboarding runs again. This cannot be undone.`}
                confirmText="Reset"
                cancelText="Cancel"
                tone="danger"
                onCancel={() => setResetTarget(null)}
                onConfirm={confirmReset}
            />
            <ConfirmDialog
                open={Boolean(deleteTarget)}
                title={
                    deleteTarget?._bulkIds
                        ? `Delete ${deleteTarget._bulkIds.length} accounts?`
                        : "Delete account"
                }
                description={
                    deleteTarget?._bulkIds
                        ? `This removes ${deleteTarget._bulkIds.length} user profiles and their recordings. This cannot be undone.`
                        : `Delete ${deleteTarget?.name}? This removes their profile and recordings.`
                }
                confirmText="Delete"
                cancelText="Cancel"
                tone="danger"
                onCancel={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
            />
            <UserDrawer
                user={drawerUser}
                onClose={() => setDrawerUser(null)}
                onToggleSuspend={handleToggleSuspend}
                onUpdate={updateUser}
                onPersist={handleSave}
                onReset={setResetTarget}
                onDelete={setDeleteTarget}
                authedFetch={authedFetch}
                setMessage={setMessage}
                currentProfile={currentProfile}
                refreshKey={drawerRefreshKey}
            />
            <BulkBar
                count={selectedIds.size}
                onClear={() => setSelectedIds(new Set())}
                onBulkPlan={handleBulkPlan}
                onBulkDelete={handleBulkDelete}
                onExportSelected={() =>
                    exportRows(users.filter((u) => selectedIds.has(u.id)))
                }
            />

            <div className="adm-users__inner">
                <Breadcrumbs
                    items={[
                        { label: "Home", href: "/" },
                        { label: "Administration", href: "/administration" },
                        {
                            label: "User management",
                            href: "/administration/users",
                        },
                    ]}
                />
                <div className="adm-users__header">
                    <div>
                        <h1 className="adm-users__title">User management</h1>
                        <p className="adm-users__sub">
                            Review accounts, update roles, and manage access to
                            Mooz.
                        </p>
                    </div>
                    <div className="adm-users__header-actions">
                        <button
                            type="button"
                            className="adm-users-btn adm-users-btn--ghost"
                            onClick={() => exportRows(filteredUsers)}
                        >
                            <IconDownload /> Export CSV
                        </button>
                    </div>
                </div>

                <div className="adm-users-stats-strip">
                    <StatCard
                        label="Total users"
                        value={loading ? "—" : totalUsers}
                        accent="default"
                    />
                    <StatCard
                        label="Pro AI"
                        value={loading ? "—" : proUsers}
                        accent="purple"
                    />
                    <StatCard
                        label="Active accounts"
                        value={loading ? "—" : activeUsers}
                        accent="green"
                    />
                    <StatCard
                        label="Total recordings"
                        value={loading ? "—" : totalRecs}
                        accent="default"
                    />
                </div>

                <section className="adm-users-panel" aria-label="User accounts">
                    <div className="adm-users-panel__header">
                        <div>
                            <h2 className="adm-users-panel__title">
                                Account review
                            </h2>
                            <p className="adm-users-panel__sub">
                                {loading
                                    ? "Loading…"
                                    : `${filteredUsers.length} of ${totalUsers} user${totalUsers !== 1 ? "s" : ""}`}
                                {selectedIds.size > 0 &&
                                    ` · ${selectedIds.size} selected`}
                            </p>
                        </div>
                        <div className="adm-users-filters">
                            <select
                                className="adm-users-select"
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(e.target.value)
                                }
                                aria-label="Filter by status"
                            >
                                <option value="all">All statuses</option>
                                <option value="active">Active</option>
                                <option value="pending">Pending</option>
                                <option value="suspended">Suspended</option>
                            </select>
                            <select
                                className="adm-users-select"
                                value={planFilter}
                                onChange={(e) => setPlanFilter(e.target.value)}
                                aria-label="Filter by plan"
                            >
                                <option value="all">All plans</option>
                                <option value="pro_ai">Pro AI</option>
                                <option value="free">Free</option>
                            </select>
                            <div className="adm-users-search-wrap">
                                <IconSearch />
                                <input
                                    ref={searchRef}
                                    className="adm-users-search"
                                    type="search"
                                    placeholder="Search users…"
                                    aria-label="Search users"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                                {query && (
                                    <button
                                        type="button"
                                        className="adm-users-search__clear"
                                        onClick={() => setQuery("")}
                                        aria-label="Clear"
                                    >
                                        <IconX />
                                    </button>
                                )}
                                <kbd
                                    className="adm-users-search__kbd"
                                    aria-hidden="true"
                                >
                                    ⌘K
                                </kbd>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="adm-users-skeleton">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div
                                    className="adm-users-skeleton__row"
                                    key={i}
                                    style={{ "--i": i }}
                                />
                            ))}
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="adm-users-empty">
                            <p className="adm-users-empty__title">
                                No users found
                            </p>
                            <p className="adm-users-empty__sub">
                                Try adjusting your filters or search term.
                            </p>
                            <button
                                type="button"
                                className="adm-users-btn adm-users-btn--ghost adm-users-btn--sm"
                                onClick={() => {
                                    setQuery("");
                                    setStatusFilter("all");
                                    setPlanFilter("all");
                                }}
                            >
                                Clear filters
                            </button>
                        </div>
                    ) : (
                        <>
                            <div
                                className="adm-users-table"
                                role="table"
                                aria-label="Users"
                            >
                                <div
                                    className="adm-users-table__head"
                                    role="row"
                                >
                                    <span role="columnheader">
                                        <input
                                            type="checkbox"
                                            className="adm-users-checkbox"
                                            checked={allPageSelected}
                                            ref={(el) => {
                                                if (el)
                                                    el.indeterminate =
                                                        somePageSelected &&
                                                        !allPageSelected;
                                            }}
                                            onChange={toggleSelectAll}
                                            aria-label="Select all on page"
                                        />
                                    </span>
                                    {TABLE_COLS.map(({ field, label }) => (
                                        <span
                                            key={label}
                                            role="columnheader"
                                            className={`adm-users-table__th${field ? " adm-users-table__th--sort" : ""}`}
                                            onClick={
                                                field
                                                    ? () => handleSort(field)
                                                    : undefined
                                            }
                                            aria-sort={
                                                field && sortField === field
                                                    ? sortDir === "asc"
                                                        ? "ascending"
                                                        : "descending"
                                                    : undefined
                                            }
                                        >
                                            {label}
                                            {field && (
                                                <SortIcon field={field} />
                                            )}
                                        </span>
                                    ))}
                                </div>
                                {pagedUsers.map((user) => (
                                    <UserRow
                                        key={user.id}
                                        user={user}
                                        selected={selectedIds.has(user.id)}
                                        onSelect={() =>
                                            setSelectedIds((prev) => {
                                                const n = new Set(prev);
                                                n.has(user.id)
                                                    ? n.delete(user.id)
                                                    : n.add(user.id);
                                                return n;
                                            })
                                        }
                                        onOpenDrawer={setDrawerUser}
                                    />
                                ))}
                            </div>
                            <Pagination
                                page={page}
                                total={filteredUsers.length}
                                pageSize={PAGE_SIZE}
                                onChange={setPage}
                            />
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
